import { RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
// import Rewrite from './MessageActions/Rewrite';
import { Message } from '@/type/chat';
import Copy from '../MessageActions/Copy';
import TextToSpeechButton from '../TTS/TextToSpeechButton';
import Like from '../MessageActions/Like';
// import More from './MessageActions/More';
import Share from '../MessageActions/Share';
import Export from '../MessageActions/Export';
import Edit from '../MessageActions/Edit';
import EditMessage from '../MessageActions/EditMessage';
import { usePathname } from 'next/navigation';
import { useMemo, useState, useCallback } from 'react'; // Added useState, useCallback
import { styled } from '@mui/material/styles'; // Added styled
import Popover from '@mui/material/Popover'; // Added Popover
import { useTheme } from 'next-themes'; // Added useTheme
import { useTranslation } from '@/lib/i18n'; // Added useTranslation
import dayjs from 'dayjs'; // Added dayjs

// Define StyledPopover outside the component to prevent re-creation on re-renders
const StyledPopover = styled(Popover)(() => ({
  '& .MuiPaper-root': {
    backgroundColor: 'var(--popover-bg, #ffffff)',
    borderRadius: '12px',
    marginTop: '10px',
    minWidth: 320,
    maxWidth: 400,
    color: 'var(--popover-text, #000000)',
    boxShadow: '0px 10px 38px -10px rgba(0, 0, 0, 0.5), 0px 10px 20px -15px rgba(0, 0, 0, 0.2)',
    border: '1px solid var(--popover-border, #e5e5e5)',
    '& .MuiList-root': {
      padding: '8px',
    },
    '& .MuiMenuItem-root': {
      fontSize: '14px',
      padding: '12px 16px',
      borderRadius: '8px',
      color: 'var(--popover-text, #000000)',
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: 'var(--popover-hover, #f5f5f5)',
      },
    },
  },
}));

export default function MessageBoxToolBar({
  rewrite,
  message,
  speechMessage,
  title,
  contentRef,
  messageIndex,
  editReport,
  toolInfoRef,
  onMessageSwitch,
  onEditMessage,
  history, // Added history prop
  onRefreshAnswer, // Added sendMessage prop
  onRegenerateAssistant, // ✅ 新增：重新生成 Assistant 回答
  isShare,
  loading = false, // ✅ 新增：loading 状态
  showRefreshAnswer = false,
}: {
  rewrite: (messageId: string) => void;
  message: Message;
  speechMessage: string;
  title: string;
  contentRef: React.RefObject<HTMLDivElement>;
  messageIndex: number;
  editReport?: (message: Message) => void;
  toolInfoRef?: React.RefObject<HTMLDivElement>;
  onMessageSwitch?: (message: Message) => void;
  onEditMessage?: (messageId: string, newContent: string, createBranch: boolean) => void;
  history?: Message[]; // Added history prop
  onRefreshAnswer?: (message: Message) => void; // Added sendMessage prop
  onRegenerateAssistant?: (assistantMessageId: string) => void; // ✅ 新增
  isShare?: boolean;
  loading?: boolean; // ✅ 新增
  showRefreshAnswer?: boolean; // ✅ 新增
  query?: string;
}) {
  const pathname = usePathname();
  const { t } = useTranslation(); // Added useTranslation
  const { theme } = useTheme(); // Added useTheme
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null); // State for Popover anchor

  const open = Boolean(anchorEl);
  const chatId = message.chatId; // Extract chatId from message prop

  // Set CSS variables for theme
  const popoverStyle = {
    '--popover-bg': theme === 'dark' ? '#1a1a1a' : '#ffffff',
    '--popover-text': theme === 'dark' ? '#ffffff' : '#000000',
    '--popover-hover': theme === 'dark' ? '#2a2a2a' : '#f5f5f5',
    '--popover-divider': theme === 'dark' ? '#333333' : '#e5e5e5',
    '--popover-border': theme === 'dark' ? '#333333' : '#e5e5e5',
  } as React.CSSProperties;

  const handleRefresh = () => {
    // ✅ 新逻辑：如果提供了 onRegenerateAssistant，直接调用它
    if (onRegenerateAssistant && message.role === 'assistant') {
      onRegenerateAssistant(message.messageId);
      return;
    }

    // ✅ 兼容旧逻辑：通过 onRefreshAnswer 重新发送用户消息
    const userMessage = history?.find((msg) => msg.messageId === message.parentMessageId);
    if (userMessage && userMessage.role === 'user') {
      onRefreshAnswer?.(userMessage);
    }
  };

  // Logic to find all alternative assistant messages for the current user query
  const alternativeAssistantMessages = useMemo(() => {
    return (
      history?.filter(
        (m) => m.role === 'assistant' && m.parentMessageId === message.parentMessageId,
      ) || []
    );
  }, [history]);

  const currentAssistantMessageIndex = useMemo(() => {
    return (
      alternativeAssistantMessages?.findIndex((msg) => msg.messageId === message.messageId) || 0
    );
  }, [alternativeAssistantMessages, message]);

  // Handle switching to a specific version
  const handleVersionChange = useCallback(
    async (selectedAssistantMessage: Message, index: number) => {
      if (!onMessageSwitch) return;

      // Call onBranchSwitch with the reconstructed messages
      onMessageSwitch(selectedAssistantMessage);
      setAnchorEl(null); // Close popover
    },
    [onMessageSwitch, t],
  );

  // Quick switch to previous/next version
  const handleQuickSwitch = useCallback(
    async (direction: 'prev' | 'next') => {
      const newIndex =
        direction === 'prev'
          ? Math.max(0, currentAssistantMessageIndex - 1)
          : Math.min(alternativeAssistantMessages.length - 1, currentAssistantMessageIndex + 1);

      if (newIndex !== currentAssistantMessageIndex) {
        await handleVersionChange(alternativeAssistantMessages[newIndex], newIndex); // Pass newIndex
      }
    },
    [currentAssistantMessageIndex, alternativeAssistantMessages, handleVersionChange],
  );

  if (isShare) {
    return (
      <div className="flex flex-row items-center justify-end w-full text-text-primary">
        <div className="flex flex-row items-center gap-2">
          <Copy
            text={`${message?.content}${
              message.sources &&
              message.sources.length > 0 &&
              `\n\nCitations:\n${message.sources
                ?.filter((s) => !!s.metadata)
                ?.map((source: any, i: any) => `[${i + 1}] ${source?.metadata?.url}`)
                .join(`\n`)}`
            }`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row items-center justify-between w-full text-text-primary">
      <div className="flex flex-row items-center gap-[8px] ml-[-10px]">
        {/* 用户消息显示编辑按钮和分支切换器 */}
        {message.role === 'user' && (
          <>
            {onEditMessage && (
              <EditMessage message={message} chatId={chatId} onEdit={onEditMessage} />
            )}
          </>
        )}
        {/* Assistant消息显示分享、导出、编辑 */}
        {message.role === 'assistant' && (
          <>
            <Share hashId={messageIndex - 1} showText query={title} chatId={chatId} />
            {/* ✅ 只有非 cancelled 且有内容的消息才显示 Export 按钮 */}
            {!message.cancelled && message.content && (
              <Export
                message={message}
                title={title}
                contentRef={contentRef}
                toolInfoRef={toolInfoRef}
              />
            )}
            {pathname.startsWith('/r/') && <Edit message={message} editReport={editReport} />}
          </>
        )}
        {/* <Rewrite rewrite={rewrite} messageId={message.messageId} /> */}
      </div>
      <div className="flex flex-row items-center gap-2">
        {/* 只在assistant消息显示点赞、复制、刷新和切换版本 */}
        {message.role === 'assistant' && (
          <>
            {/* Switch Between Answers UI for Assistant Message - styled like BranchSwitcher */}
            {alternativeAssistantMessages.length > 1 && onMessageSwitch && (
              <div className="flex items-center gap-1">
                {/* Quick switch buttons */}
                <button
                  onClick={() => handleQuickSwitch('prev')}
                  disabled={loading || currentAssistantMessageIndex === 0}
                  className={cn(
                    'p-1 rounded-sm hover:bg-[#E9E9E9] dark:dark:hover:bg-[#111111] transition-colors',
                    (loading || currentAssistantMessageIndex === 0) &&
                      'opacity-30 cursor-not-allowed',
                  )}
                  title={t('Previous version')}
                >
                  <ChevronLeft size={14} />
                </button>

                {/* Version indicator button to open Popover */}
                <button
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  disabled={loading}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1 rounded-sm hover:bg-[#E9E9E9] dark:dark:hover:bg-[#111111] transition-colors text-xs',
                    loading && 'opacity-30 cursor-not-allowed',
                  )}
                  title={t('View all versions')}
                >
                  {/* <GitBranch size={14} /> */}
                  <span>
                    {currentAssistantMessageIndex + 1}/{alternativeAssistantMessages.length}
                  </span>
                </button>

                <button
                  onClick={() => handleQuickSwitch('next')}
                  disabled={
                    loading ||
                    currentAssistantMessageIndex === alternativeAssistantMessages.length - 1
                  }
                  className={cn(
                    'p-1 rounded-sm hover:bg-[#E9E9E9] dark:dark:hover:bg-[#111111] transition-colors',
                    (loading ||
                      currentAssistantMessageIndex === alternativeAssistantMessages.length - 1) &&
                      'opacity-30 cursor-not-allowed',
                  )}
                  title={t('Next version')}
                >
                  <ChevronRight size={14} />
                </button>

                {/* Popover for version list */}
                {/* <StyledPopover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  style={popoverStyle}
                >
                  <div className="p-2">
                    <div className="text-xs text-text-secondary dark:text-text-secondary mb-2 px-2">
                      {t('Select version to view')}
                    </div>
                    <div className="max-h-[400px] overflow-y-auto space-y-1">
                      {alternativeAssistantMessages.map((altMsg, idx) => (
                        <MenuItem
                          key={altMsg.messageId}
                          onClick={() => handleVersionChange(altMsg, idx)}
                          className={cn(
                            'flex-col! items-start! gap-1! rounded-lg!',
                            idx === currentAssistantMessageIndex && 'bg-blue-50! dark:bg-blue-900/20!'
                          )}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">

                              {idx === currentAssistantMessageIndex && (
                                <span className="text-xs px-2 py-0.5 bg-blue-500 text-white rounded-sm">
                                  {t('Current')}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-text-secondary">
                              {dayjs(altMsg.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                            </div>
                          </div>
                          <div className="text-sm text-text-secondary line-clamp-2 w-full">
                            {altMsg.content}
                          </div>
                        </MenuItem>
                      ))}
                    </div>
                  </div>
                </StyledPopover> */}
              </div>
            )}
            {/* Refresh Button for Assistant Message */}
            {showRefreshAnswer && (
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={cn(
                  'text-black/70 dark:text-text-tertiary size-8 rounded-full flex items-center justify-center hover:bg-[#E9E9E9] dark:hover:bg-[#1e1e1e] transition duration-200 hover:text-black dark:hover:text-white',
                  loading && 'opacity-30 cursor-not-allowed',
                )}
                title={t('Refresh Answer')} // Use t() for translation
              >
                <RotateCcw size={16} />
              </button>
            )}

            <Like message={message} />

            <Copy
              text={`${message?.content}${
                message.sources && message.sources.length > 0
                  ? `\n\nCitations:\n${message.sources
                      ?.filter((s) => !!s.metadata)
                      ?.map((source: any, i: any) => `[${i + 1}] ${source?.metadata?.url}`) // Corrected escaping for backticks
                      .join(`\n`)}
                `
                  : ''
              }`}
            />
          </>
        )}
        {/* 朗读：生成中禁用，避免对着还在追加的正文分句 */}
        {message.role === 'assistant' && (
          <TextToSpeechButton
            id={message.messageId}
            audioSources={message?.audioSources || message.content}
            chatId={chatId}
            messageId={message.messageId}
            disabled={loading}
          />
        )}
        {/* <More message={message} /> */}
      </div>
    </div>
  );
}
